import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ReportSparePart, ReportStatus, SparePart } from '../types';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../hooks/useConfirm';
import { useReport } from '../hooks/useReport';
import { useMachine } from '../hooks/useMachine';
import { useForeignAgentReadonly } from '../hooks/useForeignAgentReadonly';
import SparePartSelectionModal from '../components/forms/SparePartSelectionModal';
import CsvImportModal from '../components/forms/CsvImportModal';
import CsvImportTooltip from '../components/forms/CsvImportTooltip';
import ImagePreviewModal from '../components/ImagePreviewModal';
import { ReportInfo, ReportStatusInfo, MachineInfo } from '../components/sidebar';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { getDashboardPath } from '../utils/navigationUtils';
import './SparePartsForm.css';

/**
 * Check if a spare part has been modified compared to its original state
 */
const isPartModified = (current: ReportSparePart, original: ReportSparePart | undefined): boolean => {
  if (!original) return true; // New part
  return (
    current.idSparePart !== original.idSparePart ||
    current.descriptionIt !== original.descriptionIt ||
    current.descriptionEn !== original.descriptionEn ||
    current.quantity !== original.quantity ||
    current.used !== original.used ||
    current.warranty !== original.warranty
  );
};

const SparePartsForm: React.FC = () => {
  const { reportId, machineId } = useParams<{ reportId: string; machineId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, NotificationContainer } = useNotification();
  const { confirm, ConfirmDialog } = useConfirm();

  const { report, loading: reportLoading, error: reportError } = useReport(reportId);
  const { machine, loading: machineLoading, error: machineError } = useMachine(machineId);
  const isReadonlyForForeignAgent = useForeignAgentReadonly(report?.customerId);

  const [spareParts, setSpareParts] = useState<ReportSparePart[]>([]);
  const [showSparePartModal, setShowSparePartModal] = useState(false);
  const [showCsvImportModal, setShowCsvImportModal] = useState(false);
  const [editingPartIndex, setEditingPartIndex] = useState<number | null>(null);
  const [sparePartCatalog, setSparePartCatalog] = useState<Record<string, SparePart>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [previewImage, setPreviewImage] = useState<{ fileName: string; base64Content?: string; imageUrl?: string } | null>(null);

  const sparePartsImageBucket = process.env.REACT_APP_SPARE_PARTS_IMAGE_BUCKET || 'https://storage.googleapis.com/';

  // Store original spare parts for dirty tracking (optimization: only save modified parts)
  const originalPartsRef = useRef<Map<string, ReportSparePart>>(new Map());

  const loading = React.useMemo(
    () => reportLoading || machineLoading,
    [reportLoading, machineLoading]
  );

  const handleSparePartSelect = useCallback((selectedPart: SparePart) => {
    if (editingPartIndex !== null) {
      setSpareParts(prev =>
        prev.map((part, index) =>
          index === editingPartIndex
            ? { ...part, idSparePart: selectedPart.id, partNumber: selectedPart.partNumber, descriptionIt: selectedPart.descriptionIt, descriptionEn: selectedPart.descriptionEn }
            : part
        )
      );
      // Update the catalog with the selected part
      setSparePartCatalog(prev => ({
        ...prev,
        [selectedPart.id]: selectedPart
      }));
    }
    setShowSparePartModal(false);
    setEditingPartIndex(null);
  }, [editingPartIndex]);

  const openSparePartModal = useCallback((index: number) => {
    setEditingPartIndex(index);
    setShowSparePartModal(true);
  }, []);

  const closeSparePartModal = useCallback(() => {
    setShowSparePartModal(false);
    setEditingPartIndex(null);
  }, []);

  useEffect(() => {
    if (reportError || (!reportLoading && !report)) {
      showError(t('messages.reportNotFound'));
      navigate('/');
    }
  }, [report, reportLoading, reportError, showError, t, navigate]);

  useEffect(() => {
    if (machineError || (!machineLoading && !machine)) {
      showError(t('messages.machineNotFound'));
      navigate(`/report/${reportId}`);
    }
  }, [machine, machineLoading, machineError, showError, t, navigate, reportId]);

  useEffect(() => {
    if (machine) {
      const parts = machine.spareParts || [];
      setSpareParts(parts);
      // Store original state for dirty tracking
      originalPartsRef.current = new Map(
        parts.filter(p => p.id).map(p => [p.id!, { ...p }])
      );
    }
  }, [machine]);

  // Fetch SparePart catalog data for existing ReportSpareParts
  useEffect(() => {
    const fetchSparePartCatalogData = async () => {
      const idsToFetch = spareParts
        .map(part => part.idSparePart)
        .filter(id => id && !sparePartCatalog[id]);

      if (idsToFetch.length === 0) return;

      const fetchedParts: Record<string, SparePart> = {};
      for (const id of idsToFetch) {
        try {
          const sparePart = await apiClient.getSparePartCatalogById(id);
          if (sparePart) {
            fetchedParts[id] = sparePart;
          }
        } catch (error) {
          console.error(`Error fetching spare part ${id}:`, error);
        }
      }

      if (Object.keys(fetchedParts).length > 0) {
        setSparePartCatalog(prev => ({ ...prev, ...fetchedParts }));
      }
    };

    fetchSparePartCatalogData();
  }, [spareParts, sparePartCatalog]);

  const handleAddRow = useCallback(() => {
    const newPart: ReportSparePart = {
      reportMachineId: machine?.id!,
      idSparePart: '',
      partNumber: '',
      descriptionIt: '',
      descriptionEn: undefined,
      quantity: 1,
      used: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setSpareParts(prev => [...prev, newPart]);
  }, [machine?.id]);

  const handleUpdatePart = useCallback((index: number, field: keyof ReportSparePart, value: any) => {
    setSpareParts(prev =>
      prev.map((part, i) =>
        i === index ? { ...part, [field]: value } : part
      )
    );
  }, []);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!machine) return false;

    setIsSaving(true);
    try {
      // Only save new parts and modified parts (optimization for performance)
      const partsToCreate: ReportSparePart[] = [];
      const partsToUpdate: ReportSparePart[] = [];

      for (const part of spareParts) {
        if (!part.id) {
          // New part (no ID yet)
          partsToCreate.push(part);
        } else {
          // Existing part - check if modified
          const original = originalPartsRef.current.get(part.id);
          if (isPartModified(part, original)) {
            partsToUpdate.push(part);
          }
        }
      }

      if (partsToCreate.length === 0 && partsToUpdate.length === 0) {
        return true;
      }

      // Save new parts and track the server-assigned IDs
      const createdPartsMap = new Map<ReportSparePart, ReportSparePart>();
      for (const part of partsToCreate) {
        const created = await apiClient.addSparePart(part);
        createdPartsMap.set(part, created);
      }

      // Update only modified parts
      for (const part of partsToUpdate) {
        await apiClient.updateSparePart(part.id!, part);
      }

      // Update local state with server-assigned IDs so re-saves don't create duplicates
      if (createdPartsMap.size > 0) {
        setSpareParts(prev => prev.map(part => createdPartsMap.get(part) ?? part));
        createdPartsMap.forEach(created => {
          if (created.id) originalPartsRef.current.set(created.id, { ...created });
        });
      }

      showSuccess(t('messages.sparePartsSavedSuccess'));
      return true;
    } catch (error: any) {
      showError(`${t('messages.errorSavingSpareParts')}: ${error.message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [machine, spareParts, showSuccess, showError, t]);

  const handleBeforeNavigate = useCallback(async (_path: string): Promise<boolean> => {
    const canSave = !!report && !isReadonlyForForeignAgent &&
      report.status !== ReportStatus.SIGNED &&
      report.status !== ReportStatus.CLOSED;
    if (canSave) {
      return handleSave();
    }
    return true;
  }, [report, isReadonlyForForeignAgent, handleSave]);

  const handleDeletePart = useCallback(async (index: number) => {
    const confirmed = await confirm({ message: t('confirmMessages.deleteSparePart') });
    if (!confirmed) return;
    try {
      const partId = spareParts[index].id;
      if (!!partId) {
        // Saved part, delete from server
        await apiClient.deleteSparePart(partId);
      }
      setSpareParts(prev => prev.filter((_, i) => i !== index));
      showSuccess(t('messages.sparePartDeletedSuccess'));
    } catch (error: any) {
      showError(`${t('messages.errorDeletingSparePart')}: ${error.message}`);
    }
  }, [spareParts, confirm, showSuccess, showError, t]);

  const reloadSpareParts = useCallback(async () => {
    if (!machine?.id) return;
    try {
      const parts = await apiClient.getSparePartsByMachine(machine.id);
      setSpareParts(parts);
      originalPartsRef.current = new Map(
        parts.filter(p => p.id).map(p => [p.id!, { ...p }])
      );
    } catch (error: any) {
      showError(t('messages.errorSavingSpareParts'));
    }
  }, [machine?.id, showError, t]);

  const handleCsvImportClose = useCallback(() => {
    setShowCsvImportModal(false);
    reloadSpareParts();
  }, [reloadSpareParts]);

  /**
   * Spare parts general editability: Editable in NEW/READY/IN_PROGRESS, Read-only in SIGNED/CLOSED
   * Controls: Add/Delete Spare Part, Quantity Shipped, Part Number, Description
   */
  const isReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED;
  }, [report, isReadonlyForForeignAgent]);

  /**
   * Quantity Used editability: Read-only in NEW/READY, Editable only in IN_PROGRESS, Read-only in SIGNED/CLOSED
   */
  const isQuantityUsedReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    return report.status !== ReportStatus.IN_PROGRESS;
  }, [report]);

  /**
   * Warranty Flag editability: Read-only in NEW/READY, Editable only in IN_PROGRESS, Read-only in SIGNED/CLOSED
   */
  const isWarrantyReadonly = React.useMemo((): boolean => {
    if (!report) return false;
    return report.status !== ReportStatus.IN_PROGRESS;
  }, [report]);

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: '', translateKey: 'breadcrumb.dashboard', path: getDashboardPath(authService.getCurrentUser()), onClick: handleBeforeNavigate },
    { label: `${t('breadcrumb.report')} #${report?.number || ''}`, path: `/report/${reportId}`, onClick: handleBeforeNavigate },
    { label: `${t('breadcrumb.machine')}: ${machine?.idNumber || ''}`, path: `/report/${reportId}/machine/${machineId}`, onClick: handleBeforeNavigate },
    { label: '', translateKey: 'breadcrumb.spareParts' }
  ];

  if (loading) {
    return <div className="loading">{t('app.loading')}</div>;
  }

  return (
    <div className="spareparts-form">
      <NotificationContainer />
      <ConfirmDialog />
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          <button onClick={handleSave} className={`btn-save ${isSaving ? 'btn-loading' : ''}`} disabled={isReadonly || isSaving}>
            {isSaving ? (
              <><span className="btn-spinner"></span> {t('common.saving')}</>
            ) : (
              <><span className="material-icons">save</span> {t('common.save')}</>
            )}
          </button>
        </div>
      </FormHeader>

      <div className="form-layout">
        {/* Main Content */}
        <div className="form-main-content">
          {/* Spare Parts Table */}
          <section className="form-section">
            <div className="section-header">
              <h3>{t('sparePartsForm.sparePartsList')}</h3>
              {!isReadonly && (
                <>
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    <button onClick={() => setShowCsvImportModal(true)} className="btn-import-csv">
                      <span className="material-icons">upload_file</span> {t('sparePartsForm.importCsv')}
                    </button>
                    <CsvImportTooltip variant="single" />
                  </div>
                  <button onClick={handleAddRow} className="btn-add">
                    <span className="material-icons">add</span> {t('sparePartsForm.addSparePart')}
                  </button>
                </>
              )}
            </div>

            <div className="spareparts-table-container">
              {spareParts.length > 0 ? (
                <table className="spareparts-table">
                  <thead>
                    <tr>
                      <th className="th-image">{t('sparePartsForm.image')}</th>
                      <th className="th-part-number">{t('sparePartsForm.partNumber')}</th>
                      <th>{t('sparePartsForm.description')}</th>
                      <th className="th-number-col">{t('sparePartsForm.quantity')}</th>
                      <th className="th-number-col">{t('sparePartsForm.used')}</th>
                      <th>{t('sparePartsForm.warranty')}</th>
                      {!isReadonly && <th>{t('common.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {spareParts.map((part, index) => (
                      <tr key={part.id || index}>
                        <td className="td-image">
                          {sparePartCatalog[part.idSparePart]?.imageBytes ? (
                            <img
                              src={`data:image/png;base64,${sparePartCatalog[part.idSparePart].imageBytes}`}
                              alt={sparePartCatalog[part.idSparePart]?.partNumber || ''}
                              className="spare-part-thumbnail spare-part-thumbnail-clickable"
                              title={t('attachmentsForm.preview')}
                              onClick={() => setPreviewImage({
                                fileName: sparePartCatalog[part.idSparePart]?.partNumber || '',
                                base64Content: sparePartCatalog[part.idSparePart].imageBytes!
                              })}
                            />
                          ) : sparePartCatalog[part.idSparePart]?.imagePath ? (
                            <img
                              src={`${sparePartsImageBucket}${sparePartCatalog[part.idSparePart].imagePath}`}
                              alt={sparePartCatalog[part.idSparePart]?.partNumber || ''}
                              className="spare-part-thumbnail spare-part-thumbnail-clickable"
                              title={t('attachmentsForm.preview')}
                              onClick={() => setPreviewImage({
                                fileName: sparePartCatalog[part.idSparePart]?.partNumber || '',
                                imageUrl: `${sparePartsImageBucket}${sparePartCatalog[part.idSparePart].imagePath}`
                              })}
                            />
                          ) : (
                            <span className="material-icons spare-part-no-image">image_not_supported</span>
                          )}
                        </td>
                        <td>
                          {isReadonly ? (
                            <span>{sparePartCatalog[part.idSparePart]?.partNumber || part.idSparePart}</span>
                          ) : (
                            <div className="input-with-button">
                              <input
                                type="text"
                                value={sparePartCatalog[part.idSparePart]?.partNumber || part.idSparePart}
                                className="table-input input-readonly"
                                readOnly
                              />
                              <button
                                type="button"
                                onClick={() => openSparePartModal(index)}
                                className="btn-select-small"
                              >
                                <span className="material-icons">search</span>
                              </button>
                            </div>
                          )}
                        </td>
                        <td>
                          <span>{i18n.language === 'it' ? part.descriptionIt : (part.descriptionEn || part.descriptionIt)}</span>
                        </td>
                        <td>
                          {isReadonly ? (
                            <span>{part.quantity}</span>
                          ) : (
                            <input
                              onChange={(e) => handleUpdatePart(index, 'quantity', e.target.value)}
                              type="number"
                              min="1"
                              value={part.quantity}
                              className="table-input-number"
                            />
                          )}
                        </td>
                        <td>
                          <input
                            onChange={(e) => handleUpdatePart(index, 'used', e.target.value)}
                            type="number"
                            min="0"
                            value={part.used}
                            className="table-input-number"
                            disabled={isQuantityUsedReadonly}
                          />
                        </td>
                        <td>
                          <input
                            onChange={(e) => handleUpdatePart(index, 'warranty', e.target.checked)}
                            type="checkbox"
                            checked={part.warranty || false}
                            className="table-input-checkbox"
                            disabled={isWarrantyReadonly}
                          />
                        </td>
                        {!isReadonly && (
                          <td>
                            <button onClick={() => handleDeletePart(index)} className="btn-delete">
                              <span className="material-icons">delete</span>
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="no-data">{t('sparePartsForm.noSpareParts')}</p>
              )}
            </div>
          </section>
        </div>

        {/* Sidebar - Right Column */}
        <aside className="form-sidebar">
          <ReportStatusInfo report={report} />
          <ReportInfo report={report} />
          <MachineInfo machine={machine} />
        </aside>
      </div>

      {showSparePartModal && (
        <SparePartSelectionModal
          onSelect={handleSparePartSelect}
          onClose={closeSparePartModal}
        />
      )}
      {showCsvImportModal && machine && (
        <CsvImportModal
          reportMachineId={machine.id}
          onClose={handleCsvImportClose}
        />
      )}
      {previewImage && (
        <ImagePreviewModal
          fileName={previewImage.fileName}
          base64Content={previewImage.base64Content}
          imageUrl={previewImage.imageUrl}
          mimeType="image/png"
          onClose={() => setPreviewImage(null)}
        />
      )}
    </div>
  );
};

export default SparePartsForm;
